import { FileOwner, getFileUrl } from './getFileUrl';

export interface OpenVisitAttachmentParams {
  visitId: string;
  /** Storage key of the attachment (the document's `filename`), NOT its stored url. */
  filename: string;
  /** Public guest surface → re-sign via the guest endpoint; otherwise the authed visit endpoint. */
  guest?: boolean;
  /** Guest session id (X-Guest-Session) — required when `guest` is true. */
  sessionId?: string;
}

export interface OpenVisitAttachmentDeps {
  resolveUrl?: (owner: FileOwner) => Promise<string>;
  openWindow?: (url: string, target?: string) => Window | null;
}

/**
 * Open a visit's "Załączniki" attachment by re-signing it on demand.
 *
 * The stored `doc.url` is a short-lived (60-min) signed URL, so opening it
 * directly breaks once it expires — and on some surfaces it isn't a usable
 * absolute URL at all, so `window.open(doc.url)` resolves against the SPA origin
 * and the router lands on the dashboard. The documents collection is the source
 * of truth, so we always re-sign via the per-entity endpoint.
 *
 * We open a blank tab synchronously (within the click gesture) to dodge popup
 * blockers, then redirect it once the fresh URL resolves. Throws on failure so
 * the caller can surface a localized error; the blank tab is closed first.
 */
export async function openVisitAttachment(
  { visitId, filename, guest = false, sessionId }: OpenVisitAttachmentParams,
  deps: OpenVisitAttachmentDeps = {},
): Promise<void> {
  const resolveUrl = deps.resolveUrl ?? getFileUrl;
  const openWindow =
    deps.openWindow ?? ((url: string, target?: string) => window.open(url, target));

  // Resolve the owner (and validate the guest session) before opening the tab,
  // so a missing sessionId is a clean programmer error rather than an orphan tab.
  let owner: FileOwner;
  if (guest) {
    if (!sessionId) {
      throw new Error(
        'openVisitAttachment: a guest sessionId is required for guest attachments',
      );
    }
    owner = { kind: 'guestVisitFile', visitId, filename, sessionId };
  } else {
    owner = { kind: 'visitFile', visitId, filename };
  }

  const newTab = openWindow('', '_blank');
  try {
    const signedUrl = await resolveUrl(owner);
    if (newTab) {
      newTab.location.href = signedUrl;
    } else {
      openWindow(signedUrl, '_blank');
    }
  } catch (error) {
    newTab?.close();
    throw error;
  }
}
